import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  RefreshCw, Database, Users2, CheckCircle2, AlertCircle, Clock, Loader2, Terminal,
} from 'lucide-react';

import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  forceGlpiSync, forceGoogleSync, obtenerEstadoSyncGoogle,
  type GlpiSyncResult, type EstadoSyncGoogle,
} from '@/services/api/sync.api';
import { getApiErrorMessage } from '@/services/api/client';
import { cn } from '@/lib/utils';

export function SyncPage() {
  const qc = useQueryClient();

  // Progreso del sync de Google: se refresca solo mientras está corriendo,
  // así la pantalla muestra barra + contadores + log en vivo sin recargar.
  const googleEstado = useQuery({
    queryKey: ['sync-google-estado'],
    queryFn: obtenerEstadoSyncGoogle,
    refetchInterval: (query) => (query.state.data?.enProgreso ? 1500 : false),
  });

  const glpi = useMutation({
    mutationFn: forceGlpiSync,
    onSuccess: (data) => {
      toast.success('Sincronización GLPI completada', { description: data.message });
      // El catálogo cambió → refrescar las vistas que dependen de él.
      qc.invalidateQueries({ queryKey: ['inventario-dashboard'] });
      qc.invalidateQueries({ queryKey: ['mantenimientos'] });
    },
    onError: (err) => {
      toast.error('Falló la sincronización con GLPI', { description: getApiErrorMessage(err) });
    },
  });

  const google = useMutation({
    mutationFn: forceGoogleSync,
    onSuccess: (data) => {
      toast.success('Sincronización iniciada', { description: data.message });
      // Arranca el polling del progreso de inmediato.
      qc.invalidateQueries({ queryKey: ['sync-google-estado'] });
    },
    onError: (err) => {
      toast.error('Falló la sincronización con Google Workspace', { description: getApiErrorMessage(err) });
    },
  });

  return (
    <div className="space-y-6">
      {/* ── Encabezado ──────────────────────────────────────────────────── */}
      <div className="space-y-1">
        <p className="text-xs font-medium uppercase tracking-widest text-primary-600">Administración</p>
        <h2 className="text-2xl font-bold tracking-tight text-foreground">Sincronización</h2>
        <p className="text-sm text-muted-foreground">
          Actualiza el catálogo de activos y el directorio de empleados desde las fuentes externas.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* ── GLPI ──────────────────────────────────────────────────────── */}
        <SyncCard
          icon={Database}
          tone="primary"
          title="Catálogo de activos (GLPI)"
          description="Importa y actualiza los equipos corporativos desde GLPI. Se ejecuta al instante y muestra el resultado."
          buttonLabel="Sincronizar con GLPI"
          pending={glpi.isPending}
          onRun={() => glpi.mutate()}
        >
          {glpi.isSuccess && <GlpiResultPanel result={glpi.data} />}
          {glpi.isError && <ErrorPanel message={getApiErrorMessage(glpi.error)} />}
        </SyncCard>

        {/* ── Google Workspace ──────────────────────────────────────────── */}
        <SyncCard
          icon={Users2}
          tone="info"
          title="Empleados (Google Workspace)"
          description="Actualiza el directorio de empleados desde Google Workspace. Corre en segundo plano y acá ves el progreso, los contadores y el log en vivo."
          buttonLabel="Sincronizar con Google"
          pending={google.isPending || googleEstado.data?.enProgreso === true}
          onRun={() => google.mutate()}
        >
          <GoogleSyncPanel
            estado={googleEstado.data}
            error={google.isError ? getApiErrorMessage(google.error) : null}
          />
        </SyncCard>
      </div>

      <p className="text-xs text-muted-foreground">
        Nota: estas sincronizaciones también corren de forma programada. Úsalas aquí solo cuando necesites
        forzar una actualización inmediata (p. ej. tras cargar equipos nuevos en GLPI).
      </p>
    </div>
  );
}

// ── Subcomponentes ──────────────────────────────────────────────────────────

const TONE = {
  primary: { bg: 'bg-primary-50', fg: 'text-primary-700', ring: 'ring-primary-100' },
  info:    { bg: 'bg-blue-50',    fg: 'text-blue-700',    ring: 'ring-blue-100' },
};

function SyncCard({
  icon: Icon, tone, title, description, buttonLabel, pending, onRun, children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  tone: keyof typeof TONE;
  title: string;
  description: string;
  buttonLabel: string;
  pending: boolean;
  onRun: () => void;
  children?: React.ReactNode;
}) {
  const t = TONE[tone];
  return (
    <Card className="flex flex-col">
      <CardHeader>
        <div className="flex items-start gap-3">
          <div className={cn('h-10 w-10 shrink-0 rounded-lg flex items-center justify-center ring-1', t.bg, t.fg, t.ring)}>
            <Icon className="h-5 w-5" />
          </div>
          <div className="space-y-1">
            <CardTitle className="text-base">{title}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1">{children}</CardContent>
      <CardFooter>
        <Button onClick={onRun} disabled={pending} className="w-full sm:w-auto">
          {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          {pending ? 'Sincronizando…' : buttonLabel}
        </Button>
      </CardFooter>
    </Card>
  );
}

function GlpiResultPanel({ result }: { result: GlpiSyncResult }) {
  const stats = [
    { label: 'Total', value: result.total },
    { label: 'Creados', value: result.creados },
    { label: 'Actualizados', value: result.actualizados },
    { label: 'Sin empleado', value: result.sinEmpleadoEnGoogle },
  ];
  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3">
      <div className="flex items-center gap-2 text-sm font-medium text-emerald-800">
        <CheckCircle2 className="h-4 w-4" />
        Última sincronización exitosa
      </div>
      <div className="mt-3 grid grid-cols-4 gap-2 text-center">
        {stats.map((s) => (
          <div key={s.label}>
            <p className="text-lg font-bold tabular text-foreground">{s.value}</p>
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{s.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function GoogleSyncPanel({ estado, error }: { estado?: EstadoSyncGoogle; error: string | null }) {
  const logRef = useRef<HTMLDivElement>(null);
  const log = estado?.log ?? [];

  // Autoscroll al final a medida que llegan líneas nuevas.
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [log.length]);

  if (!estado || estado.fase === 'inactivo') {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50/60 p-3 text-sm text-blue-800">
        <Clock className="h-4 w-4 shrink-0 mt-0.5" />
        <span>Al iniciarla vas a ver acá el progreso, los contadores y el log en vivo.</span>
      </div>
    );
  }

  const pct = estado.totalUsuarios > 0
    ? Math.round((estado.procesados / estado.totalUsuarios) * 100)
    : null;

  const faseLabel: Record<EstadoSyncGoogle['fase'], string> = {
    inactivo: 'Sin ejecutar',
    conectando: 'Conectando con Google Workspace…',
    listando: 'Listando usuarios…',
    procesando: `Procesando usuarios… ${estado.procesados}/${estado.totalUsuarios}`,
    completado: 'Sincronización completada',
    error: 'Terminó con error',
  };

  const stats = [
    { label: 'Creados', value: estado.creados },
    { label: 'Actualizados', value: estado.actualizados },
    { label: 'Sin ID', value: estado.omitidosSinCedula },
    { label: 'Duplicados', value: estado.omitidosDuplicados },
  ];

  return (
    <div className="space-y-3">
      {/* ── Barra de progreso ────────────────────────────────────────── */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-2 text-xs">
          <span className="flex items-center gap-1.5 font-medium text-foreground">
            {estado.enProgreso && <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-600" />}
            {faseLabel[estado.fase]}
          </span>
          {pct !== null && <span className="tabular text-muted-foreground">{pct}%</span>}
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
          <div
            className={cn(
              'h-full rounded-full transition-all duration-300',
              estado.fase === 'error' ? 'bg-red-500'
                : estado.fase === 'completado' ? 'bg-emerald-500'
                : 'bg-blue-600 animate-pulse',
            )}
            style={{ width: `${estado.fase === 'completado' ? 100 : Math.max(pct ?? 8, 8)}%` }}
          />
        </div>
      </div>

      {/* ── Contadores ───────────────────────────────────────────────── */}
      <div className="grid grid-cols-4 gap-2 text-center">
        {stats.map((s) => (
          <div key={s.label}>
            <p className="text-lg font-bold tabular text-foreground">{s.value}</p>
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{s.label}</p>
          </div>
        ))}
      </div>

      {/* ── Resumen final / error ────────────────────────────────────── */}
      {!estado.enProgreso && estado.mensaje && (
        <div className={cn(
          'flex items-start gap-2 rounded-lg border p-3 text-sm',
          estado.fase === 'error'
            ? 'border-red-200 bg-red-50/60 text-red-800'
            : 'border-emerald-200 bg-emerald-50/60 text-emerald-800',
        )}>
          {estado.fase === 'error'
            ? <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            : <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />}
          <span>{estado.mensaje}</span>
        </div>
      )}

      {/* ── Log en vivo ──────────────────────────────────────────────── */}
      {log.length > 0 && (
        <div>
          <p className="mb-1 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <Terminal className="h-3.5 w-3.5" />
            Log
          </p>
          <div
            ref={logRef}
            className="max-h-44 overflow-y-auto rounded-md border border-slate-800 bg-slate-950 p-2 font-mono text-[11px] leading-relaxed text-slate-200"
          >
            {log.map((linea, i) => (
              <p key={i} className="whitespace-pre-wrap break-all">{linea}</p>
            ))}
          </div>
        </div>
      )}

      {error && <ErrorPanel message={error} />}
    </div>
  );
}

function ErrorPanel({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50/60 p-3 text-sm text-red-800">
      <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
      <span>{message}</span>
    </div>
  );
}
